import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../api';

const TareaForm = ({ catedraId, onTareaCreated, onTareaUpdated, userType = 'admin', initialData = null, isEditMode = false }) => {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [puntos_posibles, setPuntosPosibles] = useState('');
  const [fecha_entrega, setFechaEntrega] = useState('');
  const [externalUrls, setExternalUrls] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingMultimediaPaths, setExistingMultimediaPaths] = useState([]); // To store existing multimedia paths
  const [planesDeClases, setPlanesDeClases] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [unidadesPlan, setUnidadesPlan] = useState([]);
  const [selectedUnidadId, setSelectedUnidadId] = useState('');

  useEffect(() => {
    if (isEditMode && initialData) {
      setTitulo(initialData.titulo || '');
      setDescripcion(initialData.descripcion || '');
      setPuntosPosibles(initialData.puntos_posibles || '');
      // Formatear la fecha para input[type="date"]
      if (initialData.fecha_entrega) {
        const date = new Date(initialData.fecha_entrega);
        setFechaEntrega(date.toISOString().split('T')[0]);
      } else {
        setFechaEntrega('');
      }

      // Asignar URLs externas y multimediaPath
      const currentExternalUrls = initialData.recursos
        ? initialData.recursos.filter(url => !url.startsWith('uploads/')).join('\n')
        : '';
      setExternalUrls(currentExternalUrls);

      const currentMultimediaPaths = initialData.recursos
        ? initialData.recursos.filter(url => url.startsWith('uploads/'))
        : [];
      setExistingMultimediaPaths(currentMultimediaPaths);
      
      // Si initialData ya tiene unidadPlanId, úsala
      if (initialData.unidadPlanId) {
        setSelectedUnidadId(initialData.unidadPlanId);
      }
      // Si no, y hay un planId en initialData, podrías necesitar cargar las unidades del plan
      // y luego seleccionar la unidad si es necesario. Por ahora, nos centramos en la carga directa.
    } else if (!isEditMode) {
      // Resetear formulario para el modo de creación
      setTitulo('');
      setDescripcion('');
      setPuntosPosibles('');
      setFechaEntrega('');
      setExternalUrls('');
      setSelectedFiles([]);
      setExistingMultimediaPaths([]);
      // Si se pasa una unidadPlanId para creación (ej. desde UnidadContentManagement),
      // asegúrate de que se preseleccione.
      if (initialData?.unidadPlanId) {
        setSelectedUnidadId(initialData.unidadPlanId);
      } else {
        setSelectedUnidadId('');
      }
    }
  }, [initialData, isEditMode]);


  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titulo.trim() || !descripcion.trim() || !puntos_posibles) {
      toast.error('Por favor, completa todos los campos obligatorios.');
      return;
    }

    let uploadedMultimediaPaths = [];
    if (selectedFiles.length > 0) {
      try {
        for (const file of selectedFiles) {
          const response = await api.uploadTareaMultimedia(file);
          uploadedMultimediaPaths.push(response.data.filePath);
        }
      } catch (error) {
        toast.error('Error al subir uno o más archivos multimedia.');
        return;
      }
    }

    const allMultimediaPaths = [...existingMultimediaPaths, ...uploadedMultimediaPaths];


    const externalUrlsArray = externalUrls.split('\n').filter(url => url.trim() !== '');
    const finalRecursos = allMultimediaPaths.length > 0 ? [...externalUrlsArray, ...allMultimediaPaths] : externalUrlsArray;

    const tareaData = {
      titulo,
      descripcion,
      puntos_posibles: parseInt(puntos_posibles, 10),
      fecha_entrega: fecha_entrega || null,
      recursos: finalRecursos,
      multimedia_path: allMultimediaPaths,
      unidadPlanId: selectedUnidadId ? parseInt(selectedUnidadId, 10) : null,
    };

    try {
      if (isEditMode) {
        await api.updateTareaForDocenteCatedra(catedraId, initialData.id, tareaData);
        toast.success('Tarea actualizada exitosamente!');
        onTareaUpdated(initialData.id, tareaData);
      } else {
        const apiFunction = userType === 'docente' ? api.createTareaForDocenteCatedra : api.createTareaForCatedra;
        const response = await apiFunction(catedraId, tareaData);

        onTareaCreated(response.data.tareaMaestra);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.error || `No se pudo ${isEditMode ? 'actualizar' : 'crear'} la tarea.`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-gray-200">
      <div>
        <label htmlFor="titulo" className="block text-sm font-medium text-gray-300">Título</label>
        <input
          type="text"
          id="titulo"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          className="mt-1 block w-full p-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-white focus:ring-purple-500 focus:border-purple-500"
          required
        />
      </div>
      <div>
        <label htmlFor="descripcion" className="block text-sm font-medium text-gray-300">Descripción (Texto Enriquecido)</label>
        <ReactQuill
          theme="snow"
          value={descripcion}
          onChange={setDescripcion}
          className="mt-1 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-white focus-within:ring-purple-500 focus-within:border-purple-500 min-h-[150px]"
        />
      </div>
      <div>
        <label htmlFor="puntos_posibles" className="block text-sm font-medium text-gray-300">Puntos Posibles</label>
        <input
          type="number"
          id="puntos_posibles"
          value={puntos_posibles}
          onChange={(e) => setPuntosPosibles(e.target.value)}
          className="mt-1 block w-full p-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-white focus:ring-purple-500 focus:border-purple-500"
          required
          min="1"
        />
      </div>

      <div>
        <label htmlFor="fecha_entrega" className="block text-sm font-medium text-gray-300">Fecha de Entrega (Opcional)</label>
        <input
          type="date"
          id="fecha_entrega"
          value={fecha_entrega}
          onChange={(e) => setFechaEntrega(e.target.value)}
          className="mt-1 block w-full p-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-white focus:ring-purple-500 focus:border-purple-500"
        />
      </div>
      <div>
        <label htmlFor="multimedia_file" className="block text-sm font-medium text-gray-300">Subir Archivos (PDF, imagen, etc.) {existingMultimediaPaths.length > 0 && <span className="text-sm text-gray-400"> (Actuales: {existingMultimediaPaths.map(path => path.split('/').pop()).join(', ')})</span>}</label>
        <input
          type="file"
          id="multimedia_file"
          onChange={handleFileChange}
          multiple
          className="mt-1 block w-full p-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-white"
        />
      </div>
      <div>
        <label htmlFor="external_urls" className="block text-sm font-medium text-gray-300">URLs Externas (una por línea)</label>
        <textarea
          id="external_urls"
          value={externalUrls}
          onChange={(e) => setExternalUrls(e.target.value)}
          rows="3"
          className="mt-1 block w-full p-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm text-white focus:ring-purple-500 focus:border-purple-500"
        ></textarea>
      </div>
      <div className="flex justify-end space-x-4 pt-4">
        <button type="submit" className="px-4 py-2 bg-purple-600 rounded hover:bg-purple-500 text-white">{isEditMode ? 'Actualizar Tarea' : 'Crear Tarea'}</button>
      </div>

    </form>
  );
};

export default TareaForm;